import { Injectable, signal } from '@angular/core';
import { FilterService } from './filter.service';
import { FilterOutputService } from './filter-output.service';
import { TabService } from './tab.service';
import { SettingsService } from './settings.service';
import { ItemDataService } from './item-data.service';

export type GearTierNavigationTarget = {
  baseType: string;
  itemClass: string;
  defenceType?: string;
};

export type JewelleryTierNavigationTarget = {
  baseType: string;
  itemClass: string;
};

export type TierListSubTabType = 'currency' | 'uniques' | 'chance-bases' | 'gear' | 'jewellery' | 'items' | 'divination-cards' | 'mods';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  readonly tierListSubTabTarget = signal<TierListSubTabType | null>(null);
  readonly gearTierNavigationTarget = signal<GearTierNavigationTarget | null>(null);
  readonly jewelleryTierNavigationTarget = signal<JewelleryTierNavigationTarget | null>(null);

  constructor(
    private filterService: FilterService,
    private filterOutputService: FilterOutputService,
    private tabService: TabService,
    private settings: SettingsService,
    private itemDataService: ItemDataService,
  ) {}

  navigateToTierListSubTab(subTab: TierListSubTabType): void {
    if (this.tabService.currentTab() !== 'tier-list') {
      this.tabService.selectTab('tier-list');
    }
    this.tierListSubTabTarget.set(subTab);
    window.history.replaceState({}, '', `/tier-list/${subTab}`);
  }

  navigateToJewelleryTier(baseType: string): void {
    if (!this.settings.enableRuleNavigation()) return;

    const flatItem = this.itemDataService.getFlatItemDataByBaseTypeName(baseType);
    if (!flatItem || !['Amulets', 'Rings', 'Belts'].includes(flatItem.itemClass)) return;

    const target: JewelleryTierNavigationTarget = {
      baseType,
      itemClass: flatItem.itemClass,
    };

    if (this.tabService.currentTab() !== 'tier-list') {
      this.tabService.selectTab('tier-list');
    }

    this.tierListSubTabTarget.set('jewellery');
    window.history.replaceState({}, '', '/tier-list/jewellery');

    setTimeout(() => {
      this.jewelleryTierNavigationTarget.set(target);
    }, 0);
  }

  navigateToGearTier(baseType: string): void {
    if (!this.settings.enableRuleNavigation()) return;

    const flatItem = this.itemDataService.getFlatItemDataByBaseTypeName(baseType);
    if (!flatItem || !this.itemDataService.getAllGearTypes().includes(flatItem.itemClass)) return;

    const defenceType = this.itemDataService.getGearDefenceType().find(i => i.name === baseType)?.defenceType;
    const target: GearTierNavigationTarget = {
      baseType,
      itemClass: flatItem.itemClass,
      defenceType: defenceType !== undefined ? String(defenceType) : undefined,
    };

    if (this.tabService.currentTab() !== 'tier-list') {
      this.tabService.selectTab('tier-list');
    }

    this.tierListSubTabTarget.set('gear');
    window.history.replaceState({}, '', '/tier-list/gear');

    setTimeout(() => {
      this.gearTierNavigationTarget.set(target);
    }, 0);
  }

  highlightBaseType(baseType: string): void {
    setTimeout(() => {
      const element = document.querySelector(`[data-base-type="${CSS.escape(baseType)}"] > .item-label`) as HTMLElement;
      if (!element) return;
      this.scrollAndHighlight(element, true);
    }, 0);
  }

  navigateToFilterTitle(ruleId: string, currencyName?: string): void {
    if (!this.settings.enableRuleNavigation()) return;

    if (ruleId === 'currency') {
      if (this.tabService.currentTab() !== 'tier-list') {
        this.tabService.selectTab('tier-list');
      }
      this.tierListSubTabTarget.set('currency');
      window.history.replaceState({}, '', '/tier-list/currency');
      if (currencyName) {
        setTimeout(() => this.navigateToCurrency(currencyName), 0);
      }
    } else if (this.tabService.currentTab() !== 'quick-filters') {
      this.tabService.selectTab('quick-filters');
      setTimeout(() => {
        this.navigateToFilterTitleInternal(ruleId);
      }, 0);
    } else {
     this.navigateToFilterTitleInternal(ruleId);
    }
  }

  private navigateToCurrency(currencyName: string): void {
    this.highlightBaseType(currencyName);
  }

  private navigateToFilterTitleInternal(ruleId: string): void {
    // Try to find the element by progressively removing parts from the end
    // Example: "gear-my-excellent-exceptional-socketed/weapon/1" -> try exact, then "gear-my-excellent-exceptional-socketed/weapon", etc.
    // until we find a match like "gear-my-excellent-exceptional"
    // Use "starts with" selector to find elements that begin with the current ruleId
    let filterTitleElement: HTMLElement | null = null;
    let currentRuleId = ruleId;
    let matchedRuleId = ruleId;
    let firstTry = true;

    while (currentRuleId && !filterTitleElement) {
      // First try exact/prefix match on the primary data-filter-rule-id attribute
      filterTitleElement = firstTry
        ? document.querySelector(`[data-filter-rule-id="${currentRuleId}"]`) as HTMLElement
        : document.querySelector(`[data-filter-rule-id^="${currentRuleId}"]`) as HTMLElement;

      // If not found via primary attribute, check elements with comma-separated data-filter-rule-ids
      if (!filterTitleElement) {
        filterTitleElement = this.findElementByRuleIds(currentRuleId, firstTry);
      }

      if (firstTry && !filterTitleElement) {
        firstTry = false;
        continue;
      }

      firstTry = false;

      if (filterTitleElement) {
        // Found a match, store the matched ruleId (use the actual attribute value)
        matchedRuleId = filterTitleElement.getAttribute('data-filter-rule-id') || currentRuleId;
      } else {
        // Remove the last part (either after '/' or after '-')
        if (currentRuleId.includes('/')) {
          // Remove last segment after '/'
          const lastSlashIndex = currentRuleId.lastIndexOf('/');
          currentRuleId = currentRuleId.substring(0, lastSlashIndex);
        } else if (currentRuleId.includes('-')) {
          // Remove last segment after '-'
          const lastDashIndex = currentRuleId.lastIndexOf('-');
          currentRuleId = currentRuleId.substring(0, lastDashIndex);
        } else {
          // No more parts to remove
          break;
        }
      }
    }

    if (!filterTitleElement) return; // not found

    const filter = this.filterService.activeFilter().filter;
    let needsUpdate = false;

    // Find the parent quick-filters-section element
    let sectionElement: HTMLElement | null = filterTitleElement.closest('.quick-filters-section');
    if (sectionElement) {
      const sectionId = sectionElement.getAttribute('data-section-id');
      if (sectionId) {
        const sectionVisibilityKey = sectionId as keyof typeof filter.sectionVisibility;

        // Check if the section is collapsed (sectionVisibility is false)
        if (filter.sectionVisibility[sectionVisibilityKey] === false) {
          filter.sectionVisibility[sectionVisibilityKey] = true;
          needsUpdate = true;
        }
      }
    }

    // Find the parent filter-group element
    let groupElement: HTMLElement | null = filterTitleElement.closest('.filter-group');
    if (groupElement) {
      const groupVisibilityId = groupElement.getAttribute('data-group-visibility-id');
      if (groupVisibilityId) {
        const groupVisibilityKey = groupVisibilityId as keyof typeof filter.groupVisibility;

        // Check if the group is collapsed (groupVisibility is false)
        if (filter.groupVisibility[groupVisibilityKey] === false) {
          filter.groupVisibility[groupVisibilityKey] = true;
          needsUpdate = true;
        }
      }
    }

    // Check if this is a my equipment/flask highlight rule (format: ruleId/equipmentType/index)
    if (ruleId.includes('/')) {
      const parts = ruleId.split('/');
      if (parts.length === 3) {
        // Find the parent equipment filter element and expand its highlights
        let equipmentFilterElement: HTMLElement | null = filterTitleElement.closest('app-weapon-filter, app-armour-filter, app-jewellery-filter, app-jewel-filter, app-flask-filter');
        if (!equipmentFilterElement) {
          // Try finding by traversing up to find the filter component
          let current: HTMLElement | null = filterTitleElement.parentElement;
          while (current && !equipmentFilterElement) {
            if (current.tagName === 'APP-WEAPON-FILTER' ||
                current.tagName === 'APP-ARMOUR-FILTER' ||
                current.tagName === 'APP-JEWELLERY-FILTER' ||
                current.tagName === 'APP-JEWEL-FILTER' ||
                current.tagName === 'APP-FLASK-FILTER') {
              equipmentFilterElement = current;
              break;
            }
            current = current.parentElement;
          }
        }

        if (equipmentFilterElement) {
          // Find the collapsed wrapper div and remove the collapsed class
          const collapsedWrapper = equipmentFilterElement.querySelector('div.collapsed');
          if (collapsedWrapper) {
            collapsedWrapper.classList.remove('collapsed');
          }
        }
      }
    }

    // Update filter if any changes were made, then scroll and highlight
    if (needsUpdate) {
      this.filterService.updateFilter();
      setTimeout(() => {
        // Re-query the element after DOM updates to ensure we have the correct reference
        // Use the matched ruleId that we found earlier
        const updatedElement = document.querySelector(`[data-filter-rule-id="${matchedRuleId}"]`) as HTMLElement;
        if (updatedElement) {
          this.scrollAndHighlight(updatedElement);
        }
      }, 0);
    } else {
      this.scrollAndHighlight(filterTitleElement);
    }
  }

  /**
   * Find an element whose data-filter-rule-ids (comma-separated) contains the target ruleId.
   * @param targetRuleId The rule ID to search for
   * @param exactMatch If true, requires exact match within the comma-separated list; otherwise uses prefix match
   */
  private findElementByRuleIds(targetRuleId: string, exactMatch: boolean): HTMLElement | null {
    const elements = document.querySelectorAll('[data-filter-rule-ids]');
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLElement;
      const ids = (el.getAttribute('data-filter-rule-ids') || '').split(',');
      const found = exactMatch
        ? ids.some(id => id === targetRuleId)
        : ids.some(id => id.startsWith(targetRuleId));
      if (found) return el;
    }
    return null;
  }

  private scrollAndHighlight(filterTitleElement: HTMLElement, highlightElementItself = false) {
    filterTitleElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Find the element with .filter-title class (could be the element itself or a child)
    // This handles both filter-title component (class on host) and filter-checkbox-group (class on inner div)
    const elementToHighlight = highlightElementItself || filterTitleElement.classList.contains('filter-title')
      ? filterTitleElement
      : filterTitleElement.querySelector('.filter-title') as HTMLElement || filterTitleElement;

    // Add highlight class
    elementToHighlight.classList.remove('highlighted');
    setTimeout(() => {
      elementToHighlight.classList.add('highlighted');
    }, 0);
  }

  /**
   * Navigate to a rule in the filter output when ctrl/cmd+clicking.
   * Accepts a single rule ID or an array of rule IDs (navigates to the first match found).
   * @param ruleIdOrIds Single rule ID or array of rule IDs to try in order
   * @param usePrefixMatch If true, uses prefix match (^=) instead of exact match. Defaults to false.
   */
  navigateToFilterOutput(ruleIdOrIds: string | string[], usePrefixMatch: boolean = false): void {
    if (!this.settings.enableRuleNavigation()) return;

    const ruleIds = Array.isArray(ruleIdOrIds) ? ruleIdOrIds : [ruleIdOrIds];
    if (ruleIds.length === 0) return;

    this.filterOutputService.ensureFormattedFilterText();
    setTimeout(() => this.scrollToFilterOutputRule(ruleIds, usePrefixMatch), 0);
  }

  private scrollToFilterOutputRule(ruleIds: string[], usePrefixMatch: boolean): void {
    const filterOutputDiv = document.querySelector('#filter-output') as HTMLElement;
    if (!filterOutputDiv) return;

    let ruleSpan: HTMLElement | null = null;

    for (const ruleId of ruleIds) {
      if (!ruleId) continue;

      if (usePrefixMatch) {
        ruleSpan = this.findRuleSpanWithPrefixMatch(filterOutputDiv, ruleId);
      } else {
        ruleSpan = filterOutputDiv.querySelector(`span[data-rule-id="${ruleId}"]`) as HTMLElement;
      }

      if (ruleSpan) break;
    }

    if (!ruleSpan) return;

    filterOutputDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    filterOutputDiv.scrollTop = ruleSpan.offsetTop - (filterOutputDiv.clientHeight / 2);

    ruleSpan.classList.remove('highlighted');
    setTimeout(() => {
      ruleSpan.classList.add('highlighted');
    }, 0);
  }

  /**
   * Find a rule span in the filter output using progressive prefix matching.
   * Tries exact match first, then progressively removes segments from the end of the ruleId.
   */
  private findRuleSpanWithPrefixMatch(filterOutputDiv: HTMLElement, ruleId: string): HTMLElement | null {
    let currentRuleId = ruleId;
    let firstTry = true;
    let ruleSpan: HTMLElement | null = null;

    while (currentRuleId && !ruleSpan) {
      ruleSpan = firstTry
        ? filterOutputDiv.querySelector(`span[data-rule-id="${currentRuleId}"]`) as HTMLElement
        : filterOutputDiv.querySelector(`span[data-rule-id^="${currentRuleId}"]`) as HTMLElement;

      if (firstTry && !ruleSpan) {
        firstTry = false;
        continue;
      }

      firstTry = false;

      if (!ruleSpan) {
        // First, try reducing tier numbers from -t5 down to -t1
        const tierMatch = currentRuleId.match(/-t(\d+)/);
        if (tierMatch) {
          const currentTier = parseInt(tierMatch[1], 10);
          if (currentTier > 1) {
            const newTier = currentTier - 1;
            currentRuleId = currentRuleId.replace(/-t\d+/, `-t${newTier}`);
            continue;
          }
        }

        // Remove the last part (either after '/' or after '-')
        if (currentRuleId.includes('/')) {
          const lastSlashIndex = currentRuleId.lastIndexOf('/');
          currentRuleId = currentRuleId.substring(0, lastSlashIndex);
        } else if (currentRuleId.includes('-')) {
          const lastDashIndex = currentRuleId.lastIndexOf('-');
          currentRuleId = currentRuleId.substring(0, lastDashIndex);
        } else {
          break;
        }
      }
    }

    return ruleSpan;
  }
}

